import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { COURS_VOITURE_ELEVE } from '../data/coursVoitureEleve';
import { QRCodeSVG } from 'qrcode.react';
import {
  User, Calendar, Clock, MapPin, TrendingUp, AlertCircle, Lightbulb,
  Send, Copy, MessageCircle, Mail, ChevronLeft, CheckCircle2, Loader2, ArrowRight,
  ClipboardList, X, Plus, Sparkles, QrCode,
} from 'lucide-react';

const COMPETENCES = [
  { code: 'C1', label: 'C1 · Maîtrise du véhicule' },
  { code: 'C2', label: 'C2 · Appréhender la route' },
  { code: 'C3', label: 'C3 · Circuler (conditions difficiles)' },
  { code: 'C4', label: 'C4 · Pratiquer (autonomie)' },
];

const THEMES_SUGGESTED = [
  'Prise en main du véhicule', 'Commandes et vérifications', 'Freinage progressif',
  'Trajectoire en virage', 'Insertion dans la circulation', 'Croisements',
  'Dépassements', 'Rond-point', 'Priorités à droite', 'Feux tricolores',
  'Changements de direction', 'Créneau (stationnement)', 'Épi', 'Bataille',
  'Marche arrière', 'Demi-tour', 'Voie rapide / autoroute', 'Route de nuit',
  'Route par pluie', 'Conduite écologique', 'Gestion du stress', 'Autonomie',
];

// Modèles pré-remplis pour gagner du temps
const TEMPLATES = [
  {
    id: 'c1-prise-en-main',
    label: 'C1 · Prise en main',
    emoji: '🚗',
    color: 'red',
    data: {
      competences: ['C1'],
      lesson_codes: ['C1a', 'C1b', 'C1c'],
      themes_travailles: ['Prise en main du véhicule', 'Commandes et vérifications', 'Freinage progressif'],
      duration_min: 60,
      conseils: 'Refais mentalement les vérifications extérieures + intérieures 3 fois cette semaine. Assieds-toi 5 min dans la voiture familiale et identifie chaque commande sans regarder.',
    },
  },
  {
    id: 'c1-manoeuvres',
    label: 'C1 · Manœuvres & stationnement',
    emoji: '🅿️',
    color: 'red',
    data: {
      competences: ['C1'],
      lesson_codes: ['C1g', 'C1h'],
      themes_travailles: ['Créneau (stationnement)', 'Épi', 'Bataille', 'Marche arrière', 'Demi-tour'],
      duration_min: 60,
      conseils: 'Repère 3 créneaux serrés près de chez toi et refais-les avec ton accompagnateur ce week-end. Vise 3 essais réussis d\'affilée.',
    },
  },
  {
    id: 'c2-ronds-points',
    label: 'C2 · Ronds-points & priorités',
    emoji: '🔄',
    color: 'amber',
    data: {
      competences: ['C2'],
      lesson_codes: ['C2c', 'C2d'],
      themes_travailles: ['Rond-point', 'Priorités à droite', 'Croisements', 'Feux tricolores'],
      duration_min: 60,
      conseils: 'Analyse les 3 principaux ronds-points de ta commune : nombre de voies, sens de contournement, sorties. Fais-les à pied avec ton accompagnateur en 15 min.',
    },
  },
  {
    id: 'c2-autoroute',
    label: 'C2 · Voie rapide & insertion',
    emoji: '🛣️',
    color: 'amber',
    data: {
      competences: ['C2'],
      lesson_codes: ['C2a', 'C2b'],
      themes_travailles: ['Insertion dans la circulation', 'Voie rapide / autoroute', 'Dépassements'],
      duration_min: 90,
      conseils: 'Sur voie rapide : accélère FRANCHEMENT dans la bretelle pour t\'insérer à la vitesse du flux. Contrôles rétros + angle mort AVANT chaque changement de voie.',
    },
  },
  {
    id: 'c3-nuit',
    label: 'C3 · Conduite de nuit',
    emoji: '🌙',
    color: 'sky',
    data: {
      competences: ['C3'],
      lesson_codes: ['C3c', 'C3d'],
      themes_travailles: ['Route de nuit', 'Gestion du stress'],
      duration_min: 60,
      conseils: 'La nuit : distance × 1,5. En cas d\'éblouissement, regarde vers le bord DROIT (ligne blanche latérale), jamais dans les phares adverses.',
    },
  },
  {
    id: 'c3-intemperies',
    label: 'C3 · Pluie / brouillard',
    emoji: '🌧️',
    color: 'sky',
    data: {
      competences: ['C3'],
      lesson_codes: ['C3a', 'C3b'],
      themes_travailles: ['Route par pluie', 'Gestion du stress'],
      duration_min: 60,
      conseils: 'Sur mouillé : distance × 1,5. Éclairage dès les premières gouttes. Aquaplaning : lever le pied, PAS de freinage, garder le cap droit.',
    },
  },
  {
    id: 'c4-autonomie',
    label: 'C4 · Autonomie & itinéraire',
    emoji: '🎯',
    color: 'emerald',
    data: {
      competences: ['C4'],
      lesson_codes: ['C4a', 'C4b'],
      themes_travailles: ['Autonomie', 'Conduite écologique'],
      duration_min: 60,
      conseils: 'Prépare mentalement l\'itinéraire AVANT de démarrer. GPS = aide, jamais chef. Signalisation prime toujours sur le GPS.',
    },
  },
  {
    id: 'examen-blanc',
    label: 'Examen blanc',
    emoji: '📝',
    color: 'red',
    data: {
      competences: ['C1', 'C2', 'C3', 'C4'],
      lesson_codes: [],
      themes_travailles: ['Autonomie', 'Voie rapide / autoroute', 'Rond-point', 'Créneau (stationnement)'],
      duration_min: 32,
      conseils: 'Reproduis les 32 min d\'examen à blanc chaque semaine : trajet imposé + 5 min d\'autonomie + 1 manœuvre + 2 vérifications techniques. Note ton score /31.',
    },
  },
];

export default function NewSessionSheetPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    student_name: '',
    student_phone: '',
    date: new Date().toISOString().slice(0, 10),
    duration_min: 60,
    lieu: '',
    competences: [],
    lesson_codes: [],
    themes_travailles: [],
    points_forts: '',
    points_a_travailler: '',
    conseils: '',
    prochaine_seance: '',
  });
  const [customTheme, setCustomTheme] = useState('');
  const [status, setStatus] = useState('idle'); // idle | creating | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [appliedTemplate, setAppliedTemplate] = useState(null);

  const applyTemplate = (tpl) => {
    setForm((f) => ({
      ...f,
      competences: [...tpl.data.competences],
      lesson_codes: [...tpl.data.lesson_codes],
      themes_travailles: [...tpl.data.themes_travailles],
      duration_min: tpl.data.duration_min ?? f.duration_min,
      conseils: f.conseils || tpl.data.conseils || '',
    }));
    setAppliedTemplate(tpl.id);
  };

  if (authLoading) return <PageShell><div className="max-w-3xl mx-auto p-12 text-white/50">Chargement…</div></PageShell>;
  if (!user || user.role !== 'enseignant') {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto p-12 text-center">
          <AlertCircle className="mx-auto text-red-400 mb-4" size={32} />
          <h2 className="font-serif-editorial text-white text-[28px]">Réservé aux enseignants ECSR</h2>
        </div>
      </PageShell>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleComp = (code) => setForm((f) => ({
    ...f,
    competences: f.competences.includes(code) ? f.competences.filter((c) => c !== code) : [...f.competences, code],
  }));
  const toggleTheme = (t) => setForm((f) => ({
    ...f,
    themes_travailles: f.themes_travailles.includes(t) ? f.themes_travailles.filter((x) => x !== t) : [...f.themes_travailles, t],
  }));
  const toggleLesson = (code) => setForm((f) => ({
    ...f,
    lesson_codes: f.lesson_codes.includes(code) ? f.lesson_codes.filter((x) => x !== code) : [...f.lesson_codes, code],
  }));
  const addCustomTheme = () => {
    const t = customTheme.trim();
    if (t && !form.themes_travailles.includes(t)) {
      setForm((f) => ({ ...f, themes_travailles: [...f.themes_travailles, t] }));
      setCustomTheme('');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_name.trim()) { setErrorMsg('Prénom élève obligatoire'); return; }
    setStatus('creating'); setErrorMsg('');
    try {
      const payload = { ...form, duration_min: parseInt(form.duration_min || 0, 10) || null };
      const r = await api.createSessionSheet(payload);
      const fullUrl = `${window.location.origin}${r.url}`;
      setResult({ ...r, fullUrl });
      setStatus('done');
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || 'Erreur lors de la création');
      setStatus('error');
    }
  };

  const copyUrl = () => {
    if (!result?.fullUrl) return;
    navigator.clipboard.writeText(result.fullUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const buildShareText = () => {
    const url = result?.fullUrl || '';
    const teacher = user?.name || 'ton moniteur';
    return `Salut ${form.student_name} !\n\nVoici la fiche de ta séance de conduite d'aujourd'hui : ${url}\n\nTu peux la consulter à tout moment. Bonne route !\n${teacher} (codevoiture.fr)`;
  };
  const openSMS = () => {
    const phone = (form.student_phone || '').replace(/\s/g, '');
    const body = encodeURIComponent(buildShareText());
    window.location.href = `sms:${phone}?body=${body}`;
  };
  const openWhatsApp = () => {
    const phone = (form.student_phone || '').replace(/[^0-9+]/g, '');
    const body = encodeURIComponent(buildShareText());
    window.open(`https://wa.me/${phone}?text=${body}`, '_blank');
  };
  const openEmail = () => {
    const subj = encodeURIComponent(`Fiche de séance conduite · ${form.date}`);
    const body = encodeURIComponent(buildShareText());
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };

  if (status === 'done' && result) {
    return (
      <PageShell>
        <section className="max-w-2xl mx-auto px-4 lg:px-6 py-10">
          <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
            <Link to="/enseignant" className="hover:text-white">Dashboard</Link>
            <span className="text-white/25">/</span>
            <Link to="/enseignant/fiches" className="hover:text-white">Fiches de séance</Link>
            <span className="text-white/25">/</span>
            <span className="text-white/70">Créée</span>
          </nav>
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-950/15 p-6 text-center" data-testid="sheet-created">
            <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-4" />
            <h2 className="font-serif-editorial text-white text-[26px]">Fiche créée ✓</h2>
            <p className="mt-2 text-white/70 text-[13.5px]">Fais scanner ce QR code par <strong className="text-white">{form.student_name}</strong> — il/elle verra directement sa fiche du jour sur son téléphone.</p>

            {/* QR Code direct vers la fiche */}
            <div className="mt-5 flex flex-col items-center gap-2">
              <div className="bg-white p-4 rounded-2xl shadow-[0_0_40px_-8px_rgba(16,185,129,0.4)]" data-testid="sheet-qr-code">
                <QRCodeSVG value={result.fullUrl} size={220} level="M" />
              </div>
              <div className="text-emerald-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
                <QrCode size={11} /> L'élève scanne · fiche affichée instantanément
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2 bg-[#0a0a0b] border border-white/15 rounded-xl px-4 py-3">
              <span className="flex-1 text-left text-white text-[13px] font-mono-spec break-all">{result.fullUrl}</span>
              <button onClick={copyUrl} className="shrink-0 h-9 px-3 rounded-full border border-white/15 text-white/85 hover:border-white/35 text-[12px] font-semibold inline-flex items-center gap-1.5" data-testid="sheet-copy-url">
                {copied ? <><CheckCircle2 size={13} className="text-emerald-400" /> Copié</> : <><Copy size={13} /> Copier</>}
              </button>
            </div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button onClick={openSMS} data-testid="sheet-share-sms" className="h-11 px-3 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 transition">
                <MessageCircle size={13} /> SMS
              </button>
              <button onClick={openWhatsApp} data-testid="sheet-share-whatsapp" className="h-11 px-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 transition">
                <Send size={13} /> WhatsApp
              </button>
              <button onClick={openEmail} data-testid="sheet-share-email" className="h-11 px-3 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 transition">
                <Mail size={13} /> Email
              </button>
              <Link to={result.url} target="_blank" data-testid="sheet-preview" className="h-11 px-3 rounded-full border border-white/20 text-white hover:border-white/40 text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition">
                Aperçu <ArrowRight size={12} />
              </Link>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
              <Link to="/enseignant/fiche-seance/nouvelle" onClick={() => window.location.reload()} className="h-10 px-4 rounded-full border border-white/15 text-white/80 hover:border-white/35 text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5">
                <Plus size={12} /> Créer une autre fiche
              </Link>
              <Link to="/enseignant/fiches" className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/15 text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5">
                Voir mes fiches
              </Link>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 lg:px-6 py-8 sm:py-12" data-testid="new-sheet-page">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/enseignant" className="hover:text-white">Dashboard</Link>
          <span className="text-white/25">/</span>
          <Link to="/enseignant/fiches" className="hover:text-white">Fiches de séance</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Nouvelle</span>
        </nav>

        <SectionTitle
          kicker="Fiche de séance · à partager par SMS / WhatsApp"
          title="Nouvelle fiche de séance"
        />
        <p className="mt-3 text-white/65 text-[14px]">Remplis rapidement les points essentiels. Un lien unique sera généré pour envoyer la fiche à l'élève — même s'il n'a pas de compte.</p>

        {/* Modèles rapides */}
        <div className="mt-6 rounded-xl border border-red-500/25 bg-red-950/10 p-4 sm:p-5" data-testid="sheet-templates">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
              <Sparkles size={11} /> Modèles rapides · 1 clic = tout pré-rempli
            </div>
            {appliedTemplate && <span className="text-emerald-300 text-[11px] font-mono-spec inline-flex items-center gap-1"><CheckCircle2 size={11} /> Modèle appliqué (tu peux modifier)</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {TEMPLATES.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => applyTemplate(tpl)}
                data-testid={`sheet-template-${tpl.id}`}
                className={`text-left px-3 py-2.5 rounded-lg border transition ${appliedTemplate === tpl.id ? 'border-red-500 bg-red-600/25' : 'border-white/10 bg-white/[0.02] hover:border-red-500/50 hover:bg-red-950/20'}`}
              >
                <div className="text-[17px]">{tpl.emoji}</div>
                <div className="mt-0.5 text-white text-[12px] font-semibold leading-tight">{tpl.label}</div>
                <div className="mt-1 text-white/45 text-[10px] font-mono-spec">{tpl.data.lesson_codes.length > 0 ? `${tpl.data.lesson_codes.length} leçon${tpl.data.lesson_codes.length > 1 ? 's' : ''}` : `${tpl.data.themes_travailles.length} thèmes`} · {tpl.data.duration_min} min</div>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6">
          {/* Identité élève */}
          <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5">
            <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3 inline-flex items-center gap-1.5"><User size={12} /> Élève</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-white/60 text-[11.5px] uppercase tracking-wider font-semibold">Prénom / Nom *</span>
                <input value={form.student_name} onChange={(e) => set('student_name', e.target.value)} required data-testid="sheet-student-name" className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white focus:border-red-500 outline-none" placeholder="ex : Léa Martin" />
              </label>
              <label className="block">
                <span className="text-white/60 text-[11.5px] uppercase tracking-wider font-semibold">Téléphone (optionnel)</span>
                <input value={form.student_phone} onChange={(e) => set('student_phone', e.target.value)} data-testid="sheet-student-phone" className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white focus:border-red-500 outline-none" placeholder="+33 6 12 34 56 78" />
              </label>
            </div>
          </div>

          {/* Séance */}
          <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5">
            <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3 inline-flex items-center gap-1.5"><Calendar size={12} /> Séance</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-white/60 text-[11.5px] uppercase tracking-wider font-semibold">Date</span>
                <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white focus:border-red-500 outline-none" />
              </label>
              <label className="block">
                <span className="text-white/60 text-[11.5px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"><Clock size={11} /> Durée (min)</span>
                <input type="number" min="0" max="480" value={form.duration_min} onChange={(e) => set('duration_min', e.target.value)} className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white focus:border-red-500 outline-none" />
              </label>
              <label className="block">
                <span className="text-white/60 text-[11.5px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"><MapPin size={11} /> Lieu (optionnel)</span>
                <input value={form.lieu} onChange={(e) => set('lieu', e.target.value)} className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white focus:border-red-500 outline-none" placeholder="ex : Perpignan centre" />
              </label>
            </div>
          </div>

          {/* Compétences REMC */}
          <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5">
            <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3 inline-flex items-center gap-1.5">Compétences REMC travaillées</div>
            <div className="flex flex-wrap gap-2">
              {COMPETENCES.map((c) => (
                <button key={c.code} type="button" onClick={() => toggleComp(c.code)} data-testid={`sheet-comp-${c.code}`}
                  className={`h-9 px-3 rounded-full border text-[12px] font-semibold transition ${form.competences.includes(c.code) ? 'border-red-500 bg-red-600 text-white' : 'border-white/15 bg-white/[0.03] text-white/70 hover:border-white/30'}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Leçons REMC détaillées (contenu complet inclus dans la fiche partagée) */}
          <div className="rounded-xl border border-red-500/25 bg-red-950/10 p-5">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
              <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
                Leçons REMC à inclure dans la fiche partagée
              </div>
              <span className="text-white/40 text-[11px] font-mono-spec">{form.lesson_codes.length} sélectionnée{form.lesson_codes.length > 1 ? 's' : ''}</span>
            </div>
            <p className="text-white/60 text-[12.5px] mb-3">Les leçons cochées apparaîtront <span className="text-red-300 font-semibold">en intégralité</span> dans la fiche envoyée à l'élève (POURQUOI, COMMENT, RISQUES + grille d'auto-évaluation).</p>
            <div className="space-y-3 max-h-[380px] overflow-y-auto pr-2">
              {COURS_VOITURE_ELEVE.map((comp) => (
                <details key={comp.code} className="rounded-lg border border-white/10 bg-[#0a0a0b]">
                  <summary className="cursor-pointer px-3 py-2.5 text-white text-[13.5px] font-semibold flex items-center justify-between hover:bg-white/[0.02]">
                    <span><span className="text-red-400 font-mono-spec">{comp.code}</span> · {comp.title || comp.titre} <span className="text-white/40 font-normal text-[12px]">({comp.lecons.length} leçons)</span></span>
                    <span className="text-white/40 text-[11px]">{comp.lecons.filter((l) => form.lesson_codes.includes(`${comp.code}${l.letter}`)).length}/{comp.lecons.length}</span>
                  </summary>
                  <div className="px-3 pb-3 pt-1 flex flex-wrap gap-1.5">
                    {comp.lecons.map((l) => {
                      const lessonCode = `${comp.code}${l.letter}`;
                      return (
                        <button key={lessonCode} type="button" onClick={() => toggleLesson(lessonCode)} data-testid={`sheet-lesson-${lessonCode}`}
                          className={`h-8 px-2.5 rounded-full border text-[11.5px] transition text-left ${form.lesson_codes.includes(lessonCode) ? 'border-red-500 bg-red-600/25 text-red-100' : 'border-white/10 bg-white/[0.02] text-white/65 hover:border-white/25 hover:text-white/90'}`}
                          title={l.titre}
                        >
                          <span className="font-mono-spec text-red-300 mr-1.5">{lessonCode}</span>{l.titre.length > 32 ? l.titre.slice(0, 32) + '…' : l.titre}
                        </button>
                      );
                    })}
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* Thèmes */}
          <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5">
            <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3 inline-flex items-center gap-1.5"><ClipboardList size={12} /> Thèmes abordés (sélectionne ceux traités)</div>
            <div className="flex flex-wrap gap-1.5">
              {THEMES_SUGGESTED.map((t) => (
                <button key={t} type="button" onClick={() => toggleTheme(t)}
                  className={`h-8 px-2.5 rounded-full border text-[11.5px] transition ${form.themes_travailles.includes(t) ? 'border-red-500 bg-red-600/20 text-red-200' : 'border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25 hover:text-white/85'}`}>
                  {t}
                </button>
              ))}
            </div>
            {form.themes_travailles.filter((t) => !THEMES_SUGGESTED.includes(t)).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {form.themes_travailles.filter((t) => !THEMES_SUGGESTED.includes(t)).map((t) => (
                  <span key={t} className="h-8 px-2.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[11.5px] inline-flex items-center gap-1.5">
                    {t}
                    <button type="button" onClick={() => toggleTheme(t)} className="text-emerald-300 hover:text-white"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <input value={customTheme} onChange={(e) => setCustomTheme(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTheme())} placeholder="Ajouter un thème personnalisé…" className="flex-1 h-10 px-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white text-[13px] focus:border-red-500 outline-none" />
              <button type="button" onClick={addCustomTheme} className="h-10 px-3 rounded-lg border border-white/20 text-white/80 hover:border-white/40 text-[12px] font-semibold inline-flex items-center gap-1"><Plus size={12} /> Ajouter</button>
            </div>
          </div>

          {/* Bilan */}
          <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5 space-y-4">
            <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5"><TrendingUp size={12} /> Bilan de la séance</div>
            <label className="block">
              <span className="text-emerald-300 text-[11.5px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"><CheckCircle2 size={11} /> Points forts</span>
              <textarea value={form.points_forts} onChange={(e) => set('points_forts', e.target.value)} rows={2} data-testid="sheet-points-forts" className="mt-1 w-full p-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white text-[13.5px] focus:border-emerald-500 outline-none resize-none" placeholder="Ex : Excellent contrôle du volant en virage, bonne prise d'information…" />
            </label>
            <label className="block">
              <span className="text-amber-300 text-[11.5px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"><AlertCircle size={11} /> Points à travailler</span>
              <textarea value={form.points_a_travailler} onChange={(e) => set('points_a_travailler', e.target.value)} rows={2} data-testid="sheet-points-atravailler" className="mt-1 w-full p-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white text-[13.5px] focus:border-amber-500 outline-none resize-none" placeholder="Ex : Anticipation freinage à revoir, contrôles rétros trop rares…" />
            </label>
            <label className="block">
              <span className="text-sky-300 text-[11.5px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"><Lightbulb size={11} /> Conseils personnalisés</span>
              <textarea value={form.conseils} onChange={(e) => set('conseils', e.target.value)} rows={2} data-testid="sheet-conseils" className="mt-1 w-full p-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white text-[13.5px] focus:border-sky-500 outline-none resize-none" placeholder="Ex : Cette semaine, entraîne-toi aux ronds-points avec ton accompagnateur…" />
            </label>
            <label className="block">
              <span className="text-white/60 text-[11.5px] uppercase tracking-wider font-semibold inline-flex items-center gap-1">Prochaine séance suggérée</span>
              <input value={form.prochaine_seance} onChange={(e) => set('prochaine_seance', e.target.value)} data-testid="sheet-prochaine" className="mt-1 w-full h-11 px-3 rounded-lg bg-[#0a0a0b] border border-white/15 text-white focus:border-red-500 outline-none" placeholder="Ex : Vendredi 10 fév, autoroute + rond-point pyrénéen" />
            </label>
          </div>

          {errorMsg && <div className="text-red-300 text-[13px]">{errorMsg}</div>}

          <div className="flex flex-col sm:flex-row gap-2">
            <button type="button" onClick={() => navigate(-1)} className="h-12 px-5 rounded-full border border-white/15 text-white/70 hover:border-white/35 text-[13px] font-semibold inline-flex items-center justify-center gap-1.5">
              <ChevronLeft size={14} /> Annuler
            </button>
            <button type="submit" disabled={status === 'creating'} data-testid="sheet-submit" className="flex-1 h-12 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 transition">
              {status === 'creating' ? <><Loader2 size={14} className="animate-spin" /> Création…</> : <>Créer & obtenir le lien partageable <ArrowRight size={14} /></>}
            </button>
          </div>
        </form>
      </section>
    </PageShell>
  );
}
